import React, { useEffect, useId, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { Id, toast } from "react-toastify";
import { ApiResponse } from "../../../interfaces/genericResponse/GenericResponse";
import { ParticipantDeactivationImpact } from "../../../services/participantServices/ParticipantService";
import "./ParticipantDeactivationModal.css";

interface ParticipantDeactivationDecision {
  openTaskCount: number;
}

interface ParticipantDeactivationModalProps {
  participantId: string;
  loadImpact: () => Promise<ApiResponse<ParticipantDeactivationImpact>>;
  onCancel: () => void;
  onConfirm: (decision: ParticipantDeactivationDecision) => void;
}

const ParticipantDeactivationModal: React.FC<
  ParticipantDeactivationModalProps
> = ({ participantId, loadImpact, onCancel, onConfirm }) => {
  const titleId = useId();
  const descriptionId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [impact, setImpact] =
    useState<ParticipantDeactivationImpact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);

    loadImpact()
      .then((response) => {
        if (!active) return;
        if (!response.success) {
          setError(response.message || "Unable to review the participant's tasks.");
          return;
        }

        if (response.data.activationTransitionInProgress) {
          setError("This participant's status is already being updated. Please wait for it to finish.");
          return;
        }

        if (response.data.isDeactivated) {
          setError("This participant is already deactivated. Refresh the page to see the latest status.");
          return;
        }

        setImpact(response.data);
      })
      .catch(() => {
        if (active) {
          setError("Unable to review the participant's tasks.");
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [loadAttempt, loadImpact]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel]);

  const taskUrl = `/tasks?page=1&participant=${encodeURIComponent(participantId)}`;
  const openTaskCount = impact?.openTaskCount ?? 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="participant-deactivation-modal relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-[#171A24]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close confirmation dialog"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500/30 dark:hover:bg-white/10 dark:hover:text-gray-100"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div className="px-6 pb-6 pt-8 sm:px-8 sm:pb-8">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 ring-1 ring-red-100 dark:bg-red-500/10 dark:ring-red-500/20">
            <AlertTriangle
              size={24}
              className="text-red-600 dark:text-red-400"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>

          <h2
            id={titleId}
            className="pr-10 font-poppins text-xl font-semibold leading-7 text-gray-900 dark:text-gray-100"
          >
            Deactivate participant?
          </h2>

          <div id={descriptionId} className="participant-deactivation-modal__copy mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
            {isLoading ? (
              <div aria-label="Checking open tasks" className="space-y-3">
                <div className="h-4 w-48 animate-pulse rounded bg-gray-200 dark:bg-white/10" />
                <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-white/10" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200 dark:bg-white/10" />
              </div>
            ) : error ? (
              <div>
                <p className="text-red-600 dark:text-red-400">{error}</p>
                <button
                  type="button"
                  onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                  className="mt-3 font-medium text-[#7E57C2] hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : openTaskCount > 0 ? (
              <p>
                This participant has{" "}
                <a
                  href={taskUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-[#6D4FB3] underline decoration-[#6D4FB3]/35 underline-offset-2 hover:text-[#563994] dark:text-violet-300 dark:hover:text-violet-200"
                >
                  {openTaskCount} {openTaskCount === 1 ? "Task" : "Tasks"}
                  <ExternalLink size={13} aria-hidden="true" />
                </a>
                . Confirming will mark {openTaskCount === 1 ? "it" : "all of them"} as COMPLETED,
                save current values for historical reference, and add a system closure
                activity. Outstanding required files will not block closure.
              </p>
            ) : (
              <p>
                Deactivation will revoke this participant&apos;s system access. No
                open tasks were found.
              </p>
            )}
          </div>

          <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              className="participant-deactivation-modal__cancel inline-flex h-11 w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 active:scale-[0.98] sm:w-auto dark:border-white/10 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isLoading || Boolean(error) || impact === null}
              onClick={() => onConfirm({ openTaskCount })}
              className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-red-600 px-5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-red-300 disabled:shadow-none sm:w-auto dark:focus:ring-offset-[#171A24]"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

class ParticipantDeactivationDialogService {
  private container: HTMLDivElement | null = null;
  private root: ReactDOM.Root | null = null;
  private activeDialog: Promise<ParticipantDeactivationDecision | null> | null = null;

  private createRoot(): ReactDOM.Root {
    this.container = document.createElement("div");
    this.container.id = "participant-deactivation-modal-root";
    document.body.appendChild(this.container);
    this.root = ReactDOM.createRoot(this.container);
    return this.root;
  }

  private removeRoot(): void {
    this.root?.unmount();
    this.container?.remove();
    this.root = null;
    this.container = null;
  }

  show(
    participantId: string,
    loadImpact: () => Promise<ApiResponse<ParticipantDeactivationImpact>>,
  ): Promise<ParticipantDeactivationDecision | null> {
    if (this.activeDialog) return this.activeDialog;

    this.activeDialog = new Promise((resolve) => {
      const root = this.createRoot();
      let resolved = false;

      const finish = (decision: ParticipantDeactivationDecision | null) => {
        if (resolved) return;
        resolved = true;
        this.removeRoot();
        this.activeDialog = null;
        resolve(decision);
      };

      root.render(
        <ParticipantDeactivationModal
          participantId={participantId}
          loadImpact={loadImpact}
          onCancel={() => finish(null)}
          onConfirm={(decision) => finish(decision)}
        />,
      );
    });

    return this.activeDialog;
  }
}

export const participantDeactivationDialog =
  new ParticipantDeactivationDialogService();

interface ConfirmParticipantDeactivationOptions {
  participantId: string;
  loadImpact: () => Promise<ApiResponse<ParticipantDeactivationImpact>>;
  deactivate: (openTaskCount: number) => Promise<ApiResponse<boolean>>;
  setOptimisticActive: (active: boolean) => void;
  setProcessing?: (processing: boolean) => void;
}

const updateProgressToast = (
  toastId: Id,
  message: string,
  type: "success" | "error",
) => {
  toast.update(toastId, {
    render: message,
    type,
    isLoading: false,
    autoClose: type === "success" ? 4000 : 6000,
    closeOnClick: true,
  });
};

export async function confirmParticipantDeactivation({
  participantId,
  loadImpact,
  deactivate,
  setOptimisticActive,
  setProcessing,
}: ConfirmParticipantDeactivationOptions): Promise<boolean> {
  const decision = await participantDeactivationDialog.show(
    participantId,
    loadImpact,
  );
  if (!decision) return false;

  setOptimisticActive(false);
  setProcessing?.(true);
  const taskLabel = decision.openTaskCount === 1 ? "1 open task" : `${decision.openTaskCount} open tasks`;
  const toastId = toast.loading(
    decision.openTaskCount > 0
      ? `Deactivating participant and completing ${taskLabel}. This may take a few minutes.`
      : "Deactivating participant. This may take a moment.",
  );

  try {
    const result = await deactivate(decision.openTaskCount);
    if (!result.success) {
      setOptimisticActive(true);
      updateProgressToast(toastId, result.message || "Participant deactivation failed.", "error");
      return false;
    }

    updateProgressToast(toastId, result.message || "Participant deactivated successfully.", "success");
    return true;
  } catch {
    setOptimisticActive(true);
    updateProgressToast(toastId, "Participant deactivation failed. The participant remains active.", "error");
    return false;
  } finally {
    setProcessing?.(false);
  }
}

interface OptimisticallyReactivateParticipantOptions {
  reactivate: () => Promise<ApiResponse<boolean>>;
  setOptimisticActive: (active: boolean) => void;
  setProcessing?: (processing: boolean) => void;
}

export async function optimisticallyReactivateParticipant({
  reactivate,
  setOptimisticActive,
  setProcessing,
}: OptimisticallyReactivateParticipantOptions): Promise<boolean> {
  setOptimisticActive(true);
  setProcessing?.(true);
  const toastId = toast.loading("Reactivating participant. This may take a moment.");

  try {
    const result = await reactivate();
    if (!result.success) {
      setOptimisticActive(false);
      updateProgressToast(toastId, result.message || "Participant reactivation failed.", "error");
      return false;
    }

    updateProgressToast(toastId, result.message || "Participant reactivated successfully.", "success");
    return true;
  } catch {
    setOptimisticActive(false);
    updateProgressToast(toastId, "Participant reactivation failed. The participant remains inactive.", "error");
    return false;
  } finally {
    setProcessing?.(false);
  }
}
